/**
 * visualServiceWorker.ts — Registration and messaging utilities for visual-sw.js.
 *
 * Usage:
 *   await registerVisualServiceWorker()   // call once on app mount
 *   const url = await mountVisual(uuid, files)
 *   // set iframe src = url
 *   unmountVisual(uuid)  // call on unmount
 */

export interface VisualFile {
  path: string
  bytes: Uint8Array
  mime: string
}

// Stored once so mountVisual can await SW readiness regardless of call order
let _readyPromise: Promise<void> | null = null

/**
 * Register the visual service worker. Safe to call multiple times —
 * subsequent calls are no-ops. Stores a promise that resolves only once the
 * SW is active AND controlling this client, so mountVisual never races.
 */
export function registerVisualServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return
  if (_readyPromise) return

  _readyPromise = (async () => {
    await navigator.serviceWorker.register('/visual-sw.js', { scope: '/' })
    // ready resolves when an active SW exists
    await navigator.serviceWorker.ready
    // clients.claim() fires controllerchange; wait for it if not yet controlling
    if (!navigator.serviceWorker.controller) {
      await new Promise<void>((resolve) => {
        navigator.serviceWorker.addEventListener('controllerchange', () => resolve(), {
          once: true,
        })
      })
    }
  })()
}

/**
 * Await SW readiness then return the active controller.
 * Always resolves because registerVisualServiceWorker waits for controllerchange.
 */
async function getController(): Promise<ServiceWorker> {
  if (_readyPromise) await _readyPromise
  return navigator.serviceWorker.controller!
}

/**
 * Mount a set of files into the service worker under /visual-proxy/{uuid}/.
 * Returns the URL to use as the iframe src.
 *
 * Must call registerVisualServiceWorker() before this.
 */
export async function mountVisual(uuid: string, files: VisualFile[]): Promise<string> {
  const sw = await getController()

  return new Promise((resolve, reject) => {
    const channel = new MessageChannel()
    channel.port1.onmessage = (e) => {
      if (e.data?.type === 'MOUNTED') {
        resolve(`/visual-proxy/${uuid}/index.html`)
      } else {
        reject(new Error('SW mount failed'))
      }
    }
    sw.postMessage({ type: 'MOUNT', uuid, files }, [channel.port2])
  })
}

/**
 * Release the files for a visual from the service worker's in-memory store.
 */
export function unmountVisual(uuid: string): void {
  const sw = navigator.serviceWorker.controller
  if (sw) sw.postMessage({ type: 'UNMOUNT', uuid })
}
